import Decimal from "decimal.js";
import { apiAxios } from "./utils";
import vars from "./vars";



/**
 * Error de validación personalizado para manejar errores de validación de modelos.
 */
interface ValidationErrors {
  [key: string]: string[];
}

class ValidationError extends Error {
  public errors: ValidationErrors;

  constructor(errors: ValidationErrors) {
    super('Validation failed');
    this.name = 'ValidationError';
    this.errors = errors;
  }
}


abstract class BaseModel {
  id: number | string | null;  // Algunos modelos pueden tener id como uuid (string), null si es nuevo

  constructor(data: any) {
    this.id = data.id || null;
  }

  toData(): any {
    const result: any = {};

    for (const key of Object.keys(this)) {
      const value: any = (this as any)[key];

      // Si el campo tiene toData(), usarlo
      if (value && typeof value.toData === "function") {
        result[key] = value.toData();
        continue;
      }

      // Si es una lista de modelos
      if (Array.isArray(value)) {
        result[key] = value.map(item =>
          item && typeof item.toData === "function"
            ? item.toData()
            : item
        );
        continue;
      }

      // Valor plano
      result[key] = value;
    }

    return result;
  }
}


class Empresa extends BaseModel {
  __str__: string;
  url: string;
  img: string;
  nombre_comercial: string;
  razon_social: string;
  identification: string;
  identification_type: string;
  telefono: string;
  website: string;
  direccion: string;
  logo: string;
  logo_height: number;
  logo_width: number;

  constructor(data: any) {
    super(data);
    this.__str__ = data.__str__;
    this.url = data.url;
    this.img = data.img;
    this.nombre_comercial = data.nombre_comercial;
    this.razon_social = data.razon_social;
    this.identification = data.identification;
    this.identification_type = data.identification_type;
    this.telefono = data.telefono;
    this.website = data.website;
    this.direccion = data.direccion;
    this.logo = data.logo;
    this.logo_height = data.logo_height;
    this.logo_width = data.logo_width;
  }
}


class Almacen extends BaseModel {
  __str__: string;
  url: string;
  img: string;
  codename: string;
  name: string;
  direccion: string;
  telefono: string;
  email: string;
  activo: boolean;
  empresa: Empresa;

  constructor(data: any) {
    super(data);
    this.__str__ = data.__str__;
    this.url = data.url;
    this.img = data.img;
    this.codename = data.codename;
    this.name = data.name;
    this.direccion = data.direccion;
    this.telefono = data.telefono;
    this.email = data.email;
    this.activo = data.activo;
    this.empresa = new Empresa(data.empresa);
  }
}


class Persona extends BaseModel {
  img: string;
  identification: string;
  identification_type: string;
  firstname: string;
  lastname: string;
  is_suplidor: boolean;
  activo: boolean;
  profile_picture: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  credito_activo: boolean;
  credito_limite: number;
  credito_tiempo: number;

  constructor(data: any) {
    super(data);
    this.img = data.img;
    this.identification = data.identification;
    this.identification_type = data.identification_type;
    this.firstname = data.firstname;
    this.lastname = data.lastname;
    this.is_suplidor = data.is_suplidor;
    this.activo = data.activo;
    this.profile_picture = data.profile_picture;
    this.email = data.email;
    this.phone = data.phone;
    this.address = data.address;
    this.credito_activo = data.credito_activo;
    this.credito_limite = data.credito_limite;
    this.credito_tiempo = data.credito_tiempo;
  }
}


class Moneda extends BaseModel {
  nombre: string;
  code: string;
  simbolo: string;
  tasa: number;
  principal: boolean;

  constructor(data: any) {
    super(data);
    this.nombre = data.nombre;
    this.code = data.code;
    this.simbolo = data.simbolo;
    this.tasa = data.tasa;
    this.principal = data.principal;
  }
}


class FormaDePago extends BaseModel {
  img: string;
  nombre: string;
  description: boolean;
  activo: boolean;

  constructor(data: any) {
    super(data);
    this.img = data.img;
    this.nombre = data.nombre;
    this.description = data.description;
    this.activo = data.activo;
  }
}


class ElectronicDocumentStatus extends BaseModel {
  document_id: string;
  status_id: string;
  status: string
  internal_status: string;
  legal_status: string;
  sequence_consumed: boolean;
  pdf_file: string | null;
  pdf_generic_url: string | null;
  pdf_pos_url: string | null;
  xml_file: string | null
  resume_xml_file: string | null;
  document_stamp_url: string | null;
  errors: string[];
  text_errors: string;
  government_response: any | null;
  list_government_response: any[] | null;
  last_api_request_date: string | null;
  create_date: string;
  update_date: string;

  constructor(data: any) {
    super(data);
    this.document_id = data.document_id;
    this.status_id = data.status_id;
    this.status = data.status;
    this.internal_status = data.internal_status;
    this.legal_status = data.legal_status;
    this.sequence_consumed = data.sequence_consumed;
    this.pdf_file = data.pdf_file;
    this.pdf_generic_url = data.pdf_generic_url || data.pdf_file;
    this.pdf_pos_url = data.pdf_pos_url;
    this.xml_file = data.xml_file;
    this.resume_xml_file = data.resume_xml_file;
    this.document_stamp_url = data.document_stamp_url;
    this.errors = data.errors;
    this.text_errors = data.text_errors;
    this.government_response = data.government_response;
    this.list_government_response = data.list_government_response;
    this.last_api_request_date = data.last_api_request_date;
    this.create_date = data.create_date;
    this.update_date = data.update_date;
  }

  get isFinished(): boolean {
    return (
      this.internal_status === vars.FINISHED &&
      [vars.ACCEPTED, vars.ACCEPTED_WITH_OBSERVATIONS, vars.REJECTED].includes(this.legal_status)
    );
  }

  get isWaiting(): boolean {
    return (
      !this.isFinished &&
      (
        this.status === vars.WAITING_RESPONSE ||
        [vars.PENDING, vars.REQUESTED, vars.FINISHED].includes(this.internal_status)
      )
    );
  }

  get isFailed(): boolean {
    return (
      this.internal_status === vars.FAILED ||
      this.status === vars.FAILED ||
      this.legal_status === vars.REJECTED
    );
  }

  get hasErrors(): boolean {
    return Boolean(this.text_errors && this.text_errors.trim().length > 0);
  }

  /**
   * Indica si se puede solicitar el ENCF
   */
  get canRequest(): boolean {
    if (this.isWaiting) return false;
    if (!this.status_id) return true;
    if (this.isFailed && this.sequence_consumed === true) return false;
    return false;
  }
}


class Ncf extends BaseModel {
  numero: string;
  serie: string;
  secuencia: string;
  tipo__nombre: string;
  status: ElectronicDocumentStatus | null;
  is_electronic: boolean;

  constructor(data: any) {
    super(data);
    this.numero = data.numero;
    this.serie = data.serie;
    this.secuencia = data.secuencia;
    this.tipo__nombre = data.tipo.nombre;
    this.status = data.status ? new ElectronicDocumentStatus(data.status) : null;
    this.is_electronic = data.serie == 'E';
  }
}


class Impuesto extends BaseModel {
  codename: string;
  name: string;
  description: string;
  value: number;
  value_type: string;
  activo: boolean;

  constructor(data: any) {
    super(data);
    this.codename = data.codename;
    this.name = data.name;
    this.description = data.description;
    this.value = data.value;
    this.value_type = data.value_type;
    this.activo = data.activo;
  }
}


class Articulo extends BaseModel {
  url: string;
  img: string;
  code: number;
  codename: string;
  description: string;
  description_html: string;
  attributes: { [key: string]: any };
  latest_price: number;
  precio_min: number;
  precio_max: number;
  descuento_max: number;
  stop_price_min: boolean;
  available: number;
  impuesto: Impuesto | null;
  activo: boolean;
  servicio: boolean;

  constructor(data: any) {
    super(data);
    this.url = data.url;
    this.img = data.img;
    this.code = data.code;
    this.codename = data.codename;
    this.description = data.description;
    this.description_html = data.description_html;
    this.attributes = data.attributes;
    this.latest_price = data.latest_price;
    this.precio_min = data.precio_min;
    this.precio_max = data.precio_max;
    this.descuento_max = data.descuento_max;
    this.stop_price_min = data.stop_price_min;
    this.available = data.available;
    this.impuesto = data.impuesto;
    this.activo = data.activo;
    this.servicio = data.servicio;
  }
}


class Movement extends BaseModel {
  url: string;
  img: string;
  numero: number;
  articulo: number | string | null;
  articulo_data: Articulo | null;  // Este campo no esta en el backend, se asigna localmente
  codename: string;
  description: string;
  cantidad: number;
  get_cantidad_devuelta: number;
  disponible: number;
  disponible_anterior: number;
  precio: number;
  precio_introduccido: number;
  precio_introduccido_con_impuesto: number;  // Este campo no esta en el backend, se asigna localmente (se usa para cálculos)
  descuento: number;
  descuento_resultante: Decimal;
  impuesto: Decimal;
  impuesto_value: number;
  impuesto_ya_incluido: boolean;  // TODO: en desuso en el backend, se planea eliminar
  subtotal: Decimal = new Decimal(0);  // campo calculado localmente (no viene del backend)
  total: Decimal;
  //total_local: number;  // TODO: en desuso en el backend, se planea eliminar
  warranty: any | null;
  serials: string[];
  create_user: number;
  update_user: number;
  create_date: string;
  update_date: string;
  pendingServerSave: boolean = false;

  constructor(data: any) {
    super(data);
    this.url = data.url;
    this.img = data.img;
    this.numero = data.numero;
    this.articulo = data.articulo;
    this.articulo_data = data.articulo_data ? new Articulo(data.articulo_data) : null;
    this.codename = data.codename;
    this.description = data.description;
    this.cantidad = parseFloat(data.cantidad);
    this.get_cantidad_devuelta = parseFloat(data.get_cantidad_devuelta);
    this.disponible = parseFloat(data.disponible);
    this.disponible_anterior = parseFloat(data.disponible_anterior);
    this.precio = parseFloat(data.precio);
    this.precio_introduccido = parseFloat(data.precio_introduccido);
    this.descuento = parseFloat(data.descuento);
    this.descuento_resultante = Decimal(data.descuento_resultante || 0);
    this.impuesto = Decimal(data.impuesto || 0);
    this.impuesto_value = parseFloat(data.impuesto_value);
    this.impuesto_ya_incluido = data.impuesto_ya_incluido;
    this.total = new Decimal(data.total);
    this.warranty = data.warranty;
    this.serials = data.serials;
    this.create_user = data.create_user;
    this.update_user = data.update_user;
    this.create_date = data.create_date;
    this.update_date = data.update_date;

    if (this.impuesto_ya_incluido) {
      // Versión antigua ya no usada pero mantenida para compatibilidad
      this.precio_introduccido_con_impuesto = this.precio_introduccido;
    } else {
      // Calcular precio con impuesto
      this.precio_introduccido_con_impuesto = new Decimal(this.precio_introduccido)
        .mul(new Decimal(1).add(new Decimal(this.impuesto_value).div(100)))
        .toNumber();
    }

    // Calcular totales iniciales
    this.calculateTotals(this.impuesto_ya_incluido);
  }

  /**
   * Calcula total e impuesto de la línea y sincroniza los precios introducidos.
   * - includeTax: indica si la UI estaba enviando precio con impuesto.
   * - Mantiene la fuente sin mutar hasta el final.
   * - Descuento se aplica sobre base neta; impuesto se calcula sobre base neta menos descuento.
   */
  calculateTotals(includeTax: boolean = false): void {
    const D = (v: any) => new Decimal(v ?? 0);

    // Entradas
    const rate = D(this.impuesto_value ?? 0); // porcentaje, ej. 18
    const qty = D(this.cantidad ?? 0);
    const inputNet = D(this.precio_introduccido ?? 0);
    const inputGross = D(this.precio_introduccido_con_impuesto ?? 0);
    const discountPct = D(this.descuento ?? 0);

    // No mutar hasta el final: calcular unidad neta y bruta según lo introducido
    let unitNet = D(0);
    let unitGross = D(0);

    if (includeTax) {
      // Usuario introdujo precio con impuesto
      unitGross = inputGross;
      unitNet = rate.gt(0) ? unitGross.div(rate.div(100).plus(1)) : unitGross;
    } else {
      // Usuario introdujo precio neto (o legacy marcado como ya incluido)
      unitNet = inputNet;
      if (this.impuesto_ya_incluido && rate.gt(0)) {
        // Registro legacy: el campo precio_introduccido contiene el bruto
        unitGross = inputNet;
        unitNet = unitGross.div(rate.div(100).plus(1));
      } else {
        unitGross = rate.gt(0) ? unitNet.mul(rate.div(100).plus(1)) : unitNet;
      }
    }

    // Cálculos de línea
    const lineNet = unitNet.mul(qty);                            // subtotal antes de descuento
    const discountAmount = lineNet.mul(discountPct).div(100);    // descuento sobre neto
    const taxableBase = lineNet.minus(discountAmount);           // base imponible
    const taxAmount = rate.gt(0) ? taxableBase.mul(rate).div(100) : D(0); // impuesto sobre la base imponible
    const lineTotal = taxableBase.plus(taxAmount);               // total final de la línea

    // Guardar valores (precios unitarios como números para UI; importes financieros como Decimal para máxima precisión)
    // Redondear a 4 decimales para cumplir con la restricción del backend.
    this.precio_introduccido = parseFloat(unitNet.toFixed(4));
    this.precio_introduccido_con_impuesto = unitGross.toNumber();

    // Mantener Decimals para que el documento pueda agregarlos con precisión (si la app espera números, convertir con .toNumber())
    this.impuesto = taxAmount;
    this.descuento_resultante = discountAmount;
    this.subtotal = lineNet;
    this.total = lineTotal;
  }
}


class Transaction extends BaseModel {
  url: string;
  img: string;
  account: number;
  documento_balance_anterior: number;
  tipo: string;
  monto: number;
  monto_local: number;
  fecha: string;
  forma_de_pago: number;
  forma_de_pago_nombre: string;
  concepto: string;
  nota: string;
  create_user: number;
  update_user: number;
  create_date: string;
  update_date: string;
  pendingServerSave: boolean = false;

  constructor(data: any) {
    super(data);
    this.url = data.url;
    this.img = data.img;
    this.account = data.account;
    this.documento_balance_anterior = data.documento_balance_anterior;
    this.tipo = data.tipo;
    this.monto = data.monto;
    this.monto_local = data.monto_local;
    this.fecha = data.fecha;
    this.forma_de_pago = data.forma_de_pago;
    this.forma_de_pago_nombre = data.forma_de_pago_nombre;
    this.concepto = data.concepto;
    this.nota = data.nota;
    this.create_user = data.create_user;
    this.update_user = data.update_user;
    this.create_date = data.create_date;
    this.update_date = data.update_date;
  }

  toApiPayload(document: Document): any {

    // Para compras | contabilidad débito, cambio de signo en el monto
    let monto = this.monto;
    if (document.isCuentaPorPagar) {
      monto = -Math.abs(this.monto);
    }

    return {
      documento: document.id,
      account: this.account,
      tipo: this.tipo,
      monto: monto,
      fecha: this.fecha,
      forma_de_pago: this.forma_de_pago,
      concepto: this.concepto,
      nota: this.nota,
    };
  }
}


class File extends BaseModel {
  name: string;
  description: string;
  ext: string;
  size: number;
  url: string;
  file: string;

  constructor(data: any) {
    super(data);
    this.name = data.name;
    this.description = data.description;
    this.ext = data.ext;
    this.size = data.size;
    this.url = data.url;
    this.file = data.file;
  }
}


class DocumentType extends BaseModel {
  codename: string;
  name: string;
  clase: string;
  almacen: any | null;
  inventario: string;
  tipo_comprobante_fiscal: number | null;
  is_electronic: boolean;
  activo: boolean;
  sistema: boolean;
  print_promo_img: string | null;
  print_sello_img: string | null;
  print_user_firma: string | null;

  constructor(data: any) {
    super(data);
    this.codename = data.codename;
    this.name = data.name;
    this.clase = data.clase;
    this.almacen = data.almacen;
    this.inventario = data.inventario;
    this.tipo_comprobante_fiscal = data.tipo_comprobante_fiscal;
    this.is_electronic = data.is_electronic;
    this.activo = data.activo;
    this.sistema = data.sistema;
    this.print_promo_img = data.print_promo_img;
    this.print_sello_img = data.print_sello_img;
    this.print_user_firma = data.print_user_firma;
  }
}


abstract class DocumentMixin extends BaseModel {
  // Contrato mínimo requerido por el mixin
  abstract fecha_vence: string | null;
  abstract balance: number;
  abstract q_total: number;
  abstract tipo_clase: string;

  static _apiUrl: string = '/api/documento/documento/';  // FIXME: Esto debe ser obtenido del backend en el futuro

  /**
   * Obtiene un documento desde el backend por su ID.
   * @param id ID del documento a obtener.
   */
  static async fetch(id: number | string): Promise<Document> {
    const response = await apiAxios.get(`${this._apiUrl}${id}/`);
    const data = response.data;
    return new Document(data);
  }

  /**
   * Actualiza el modelo actual con datos frescos desde el backend.
   */
  refreshFromBackend(): void {
    Object.assign(this, Document.fetch(this.id!));
  }

  /**
   * Indica si el documento está vencido.
   * Un documento se considera vencido si la fecha de vencimiento ha pasado y el balance es mayor a cero.
   * Tener en cuenta que el balance pendiente es negativo si es factura (cliente) pero positivo si es compra (suplidor).
   */
  get isOverdue(): boolean {
    if (!this.fecha_vence) return false;
    const today = new Date();
    const dueDate = new Date(this.fecha_vence);

    if (today > dueDate) {
      // Verificar el balance según el tipo de documento
      if (this.isCuentaPorCobrar && this.balance < 0) {
        return true;  // Factura vencida con balance pendiente
      } else if (this.isCuentaPorPagar && this.balance > 0) {
        return true;  // Compra vencida con balance pendiente
      }
    }
    return false;
  }

  get isPaid(): boolean {
    return this.q_total !== 0 && this.balance === 0;
  }

  // Alineado con el backend `DocumentoCuentaPorPagarManager`
  get isCuentaPorPagar(): boolean {
    return ['COMPRA', 'CONTABILIDAD_DEBITO'].includes(this.tipo_clase);
  }

  // Alineado con el backend `DocumentoCuentaPorCobrarManager`
  get isCuentaPorCobrar(): boolean {
    return ['FACTURA_CONTADO', 'FACTURA_CREDITO', 'CONTABILIDAD_CREDITO'].includes(this.tipo_clase);
  }
}


/**
 * Versión ligera del modelo Document para listas y referencias simples.
 */
class DocumentLite extends DocumentMixin {
  id: number;
  img: string;
  secuencia: number;
  numero: string;
  tipo: number;
  tipo_clase: string;
  fecha: string;
  fecha_vence: string | null;
  persona: number | string | null;
  persona_nombre: string;
  persona_identification: string;
  persona_img: string | null;
  moneda: number;
  moneda_simbolo: string;
  ncf: number | null;
  suplidor_ncf: string;  // Para documentos de compra
  ledger_state: any | null;
  q_descuento: number;
  q_impuesto: number;
  q_total: number;
  q_total_local: number;
  balance: number;
  status: string;
  is_borrador: boolean;
  create_user: number;
  create_date: string;
  update_user: number | null;
  update_date: string;

  constructor(data: any) {
    super(data);
    this.id = data.id;
    this.img = data.img;
    this.secuencia = data.secuencia;
    this.numero = data.numero;
    this.tipo = data.tipo;
    this.tipo_clase = data.tipo_clase;
    this.fecha = data.fecha;
    this.fecha_vence = data.fecha_vence;
    this.persona = data.persona;
    this.persona_img = data.persona_img;
    this.persona_nombre = data.persona_nombre;
    this.persona_identification = data.persona_identification;
    this.moneda = data.moneda;
    this.moneda_simbolo = data.moneda_simbolo;
    this.ncf = data.ncf;
    this.suplidor_ncf = data.suplidor_ncf;
    this.ledger_state = data.ledger_state || null;
    this.q_descuento = data.q_descuento;
    this.q_impuesto = data.q_impuesto;
    this.q_total = data.q_total;
    this.q_total_local = data.q_total_local;
    this.balance = data.balance;
    this.status = data.status;
    this.is_borrador = data.is_borrador;
    this.create_user = data.create_user;
    this.create_date = data.create_date;
    this.update_user = data.update_user;
    this.update_date = data.update_date;
  }

  get detailUrl(): string {  // TODO: Esto debería venir del backend
    return `/documento/document/app/devolucion/${this.id}/`;
  }
}


class Document extends DocumentMixin {
  // Estos campos no vienen del backend, se calculan localmente
  // Desglose de impuestos por tasa
  taxes_breakdown = {} as { [taxRate: string]: { taxableBase: Decimal; taxAmount: Decimal } };

  __str__: string;
  url: string;
  img: string;
  secuencia: number;
  numero: string;
  almacen: number;
  almacen_data: Almacen;
  almacen_transferencia: number | null;
  tipo: number;
  tipo_clase: string;
  tipo_data: DocumentType;
  fecha: string
  fecha_vence: string | null;
  reference_number: string;
  persona: number | string | null;
  persona_nombre: string;
  persona_data: Persona | null;
  moneda: number;
  moneda_tasa: number;
  moneda_simbolo: string;
  nota: string;
  descuento: number;
  descuento_tipo: string | null;  // TODO: en desuso en el backend, se planea eliminar
  cerrado: boolean;
  is_borrador: boolean;
  is_solo_lectura: boolean;
  is_pagado: boolean;
  ncf: Ncf | null;
  paga_impuesto: boolean;
  suplidor_ncf: string;
  ledger_state: any | null;
  forma_de_pago: number | null;
  create_user: number;
  update_user: number;
  create_date: string;
  update_date: string;
  q_last_update: string;
  q_tasa: number;
  q_importe: number;
  q_importe_local: number;
  q_descuento: number;
  q_descuento_local: number;
  q_importe_con_descuento: number;
  q_importe_con_descuento_local: number;
  q_impuesto: number;
  q_impuesto_local: number;
  q_total: number;
  q_total_local: number;
  q_cantidad_movimientos: number;
  payments: number;
  balance: number;
  aplica_para_pagos: boolean;
  aplica_para_devoluciones: boolean;
  movements: Movement[];
  transactions: Transaction[];
  devoluciones: DocumentLite[];
  files: File[] | null;
  task_results: any[];

  constructor(data: any) {
    super(data);
    this.__str__ = data.__str__;
    this.url = data.url;
    this.img = data.img;
    this.secuencia = data.secuencia;
    this.numero = data.numero;
    this.almacen = data.almacen;
    this.almacen_data = new Almacen(data.almacen_data);
    this.almacen_transferencia = data.almacen_transferencia;
    this.tipo = data.tipo;
    this.tipo_clase = data.tipo_clase;
    this.tipo_data = new DocumentType(data.tipo_data);
    this.fecha = data.fecha;
    this.fecha_vence = data.fecha_vence;
    this.reference_number = data.reference_number;
    this.persona = data.persona;
    this.persona_nombre = data.persona_nombre;
    this.persona_data = data.persona_data ? new Persona(data.persona_data) : null;
    this.moneda = data.moneda;
    this.moneda_tasa = data.moneda_tasa;
    this.moneda_simbolo = data.moneda_simbolo;
    this.nota = data.nota;
    this.descuento = data.descuento;
    this.descuento_tipo = data.descuento_tipo;
    this.cerrado = data.cerrado;
    this.is_borrador = data.is_borrador;
    this.is_solo_lectura = data.is_solo_lectura;
    this.is_pagado = data.is_pagado;
    this.ncf = data.ncf ? new Ncf(data.ncf) : null;
    this.paga_impuesto = data.paga_impuesto;
    this.suplidor_ncf = data.suplidor_ncf;
    this.ledger_state = data.ledger_state || null;
    this.forma_de_pago = data.forma_de_pago || null;
    this.create_user = data.create_user;
    this.update_user = data.update_user;
    this.create_date = data.create_date;
    this.update_date = data.update_date;
    this.q_last_update = data.q_last_update;
    this.q_tasa = data.q_tasa;
    this.q_importe = data.q_importe;
    this.q_importe_local = data.q_importe_local;
    this.q_descuento = data.q_descuento;
    this.q_descuento_local = data.q_descuento_local;
    this.q_importe_con_descuento = data.q_importe_con_descuento;
    this.q_importe_con_descuento_local = data.q_importe_con_descuento_local;
    this.q_impuesto = data.q_impuesto;
    this.q_impuesto_local = data.q_impuesto_local;
    this.q_total = data.q_total;
    this.q_total_local = data.q_total_local;
    this.q_cantidad_movimientos = data.q_cantidad_movimientos;
    this.payments = data.payments;
    this.balance = data.balance;
    this.aplica_para_pagos = data.aplica_para_pagos;
    this.aplica_para_devoluciones = data.aplica_para_devoluciones;
    this.movements = data.movements.map((movData: any) => new Movement(movData));
    this.transactions = (data.transaccion_set || data.transactions).map((tranData: any) => new Transaction(tranData));  // Soporta ambos nombres de campo pues se pretenden cambiar el nombre en el backend a "transactions"
    this.files = data.files ? data.files.map((fileData: any) => new File(fileData)) : null;
    this.devoluciones = data.devoluciones ? data.devoluciones.map((docData: any) => new DocumentLite(docData)) : [];
    this.task_results = data.task_results;

    // Calcular totales iniciales
    this.calculateTotals();
  }

  /**
   * Indica si el documento tiene devoluciones asociadas.
   */
  get hasReturns(): boolean {
    return this.devoluciones && this.devoluciones.length > 0;
  }

  /**
   * Indica si se puede crear una devolución para este documento.
   * Una devolución puede crearse si el documento permite devoluciones y no está completamente devuelto.
   */
  get canCreateReturn(): boolean {
    if (!this.aplica_para_devoluciones) return false;

    // Verificar si hay cantidades disponibles para devolución
    for (const mov of this.movements as Movement[] || []) {
      const cantidadOriginal = mov.cantidad ?? 0;
      const cantidadDevuelta = mov.get_cantidad_devuelta ?? 0;

      if (cantidadDevuelta < cantidadOriginal) {
        return true;  // Al menos un artículo tiene cantidad disponible para devolución
      }
    }

    return false;  // Todas las cantidades ya han sido devueltas
  }

  /**
   * Valida el modelo antes de enviarlo al backend.
   * Lanza ValidationError si hay errores de validación.
   */
  validate(): void {
    const errors: ValidationErrors = {};

    if (!this.almacen) errors['almacen'] = ['El almacén no está definido.'];
    if (!this.tipo) errors['tipo'] = ['El tipo de documento no está definido.'];
    if (!this.fecha) errors['fecha'] = ['La fecha no está definida.'];
    if (!this.moneda) errors['moneda'] = ['La moneda no está definida.'];

    this.movements?.forEach((mov, index) => {
      if (!mov.articulo) errors[`movements.${index}.articulo`] = ['Artículo no definido.'];
      if (mov.cantidad <= 0) errors[`movements.${index}.cantidad`] = ['Cantidad debe ser mayor a 0.'];
      if (mov.precio_introduccido < 0) errors[`movements.${index}.precio_introduccido`] = ['Precio introducido no puede ser negativo.'];
    });

    if (Object.keys(errors).length > 0) {
      throw new ValidationError(errors);
    }
  }

  toApiPayload(): any {
    return {
      almacen: this.almacen,
      almacen_transferencia: this.almacen_transferencia,
      tipo: this.tipo,
      fecha: this.fecha,
      fecha_vence: this.fecha_vence,
      reference_number: this.reference_number,
      persona: this.persona,
      persona_nombre: this.persona_nombre,
      moneda: this.moneda,
      moneda_tasa: this.moneda_tasa,
      nota: this.nota,
      descuento: this.descuento || 0,  // Asegurar que no sea null
      paga_impuesto: this.paga_impuesto,
      suplidor_ncf: this.suplidor_ncf,
      forma_de_pago: this.forma_de_pago,
      movements: this.movements.map(mov => {
        return {
          id: mov.id,
          articulo: mov.articulo,
          description: mov.description,
          cantidad: mov.cantidad,
          precio_introduccido: parseFloat(mov.precio_introduccido.toFixed(4)),
          descuento: mov.descuento,
          impuesto_ya_incluido: mov.impuesto_ya_incluido,
          serials: mov.serials,
          is_dirty: mov.pendingServerSave,  // Indica si el movimiento fue modificado localmente y debe ser procesado en el backend (el backend espera este campo)
        };
      }),
    };
  }

  /**
   * Calcula los totales del documento sumando los totales de cada movimiento.
   * Soporta descuento global o por movimiento (mutuamente excluyentes).
   */
  calculateTotals() {
    const descuentoDelDocumento = this.get_descuento_del_documento_base();
    const descuentoDeMovimientos = this.get_descuento_de_los_movimientos();

    if (descuentoDelDocumento.gt(0) && descuentoDeMovimientos.gt(0)) {
      throw new Error(
        "No se permite un descuento global si existen movimientos con descuento individual."
      );
    }

    let subtotal = new Decimal(0);
    let totalImpuesto = new Decimal(0);
    let total = new Decimal(0);
    let totalLocal = new Decimal(0);

    const taxes_breakdown: {
      [taxRate: string]: { taxableBase: Decimal; taxAmount: Decimal };
    } = {};

    // 1. Subtotal bruto
    this.movements.forEach(mov => {
      subtotal = subtotal.plus(mov.subtotal);
    });

    // 2. Descuento total
    const descuentoTotal = descuentoDelDocumento.gt(0)
      ? descuentoDelDocumento
      : descuentoDeMovimientos;

    // Factor para prorratear descuento global
    const prorrateoFactor =
      descuentoDelDocumento.gt(0) && subtotal.gt(0)
        ? new Decimal(1).minus(descuentoDelDocumento.div(subtotal))
        : new Decimal(1);

    // 3. Impuestos
    this.movements.forEach(mov => {
      let baseImponible = mov.subtotal;

      if (descuentoDelDocumento.gt(0)) {
        // Descuento global prorrateado
        baseImponible = baseImponible.times(prorrateoFactor);
      } else {
        // Descuento por movimiento
        baseImponible = baseImponible.minus(mov.descuento_resultante);
      }

      if (this.paga_impuesto) {
        const taxAmount = baseImponible
          .times(new Decimal(mov.impuesto_value))
          .div(100);

        totalImpuesto = totalImpuesto.plus(taxAmount);

        const taxRateKey = mov.impuesto_value.toString();
        if (!taxes_breakdown[taxRateKey]) {
          taxes_breakdown[taxRateKey] = {
            taxableBase: new Decimal(0),
            taxAmount: new Decimal(0),
          };
        }

        taxes_breakdown[taxRateKey].taxableBase =
          taxes_breakdown[taxRateKey].taxableBase.plus(baseImponible);
        taxes_breakdown[taxRateKey].taxAmount =
          taxes_breakdown[taxRateKey].taxAmount.plus(taxAmount);
      }
    });

    // 4. Total
    total = subtotal.minus(descuentoTotal).plus(totalImpuesto);

    // 5. Moneda local
    totalLocal =
      this.moneda_tasa && this.moneda_tasa !== 1
        ? total.times(new Decimal(this.moneda_tasa))
        : total;

    // 6. Asignación final
    this.q_importe = subtotal.toNumber();
    this.q_descuento = descuentoTotal.toNumber();
    this.q_impuesto = totalImpuesto.toNumber();
    this.q_total = total.toNumber();
    this.q_total_local = totalLocal.toNumber();
    this.taxes_breakdown = taxes_breakdown;
  }

  /**
   * Calcula el descuento global usando el subtotal real.
   */
  get_descuento_del_documento_base(): Decimal {
    if (!this.descuento) return new Decimal(0);

    const subtotal = this.movements.reduce(
      (acc, mov) => acc.plus(mov.subtotal),
      new Decimal(0)
    );

    if (this.descuento_tipo === "PORCENTAJE" || !this.descuento_tipo) {
      return subtotal.times(new Decimal(this.descuento).div(100));
    }

    return new Decimal(this.descuento);
  }

  /**
   * Sumatoria de descuentos por movimiento.
   */
  get_descuento_de_los_movimientos(): Decimal {
    return this.movements.reduce(
      (acc, mov) => acc.plus(mov.descuento_resultante),
      new Decimal(0)
    );
  }


}


export { Articulo, Document, DocumentLite, DocumentType, ElectronicDocumentStatus, FormaDePago, Impuesto, Moneda, Movement, Ncf, Persona, Transaction, ValidationError };

